/**
 * A 3D nézet rétegei: a Unity `WebInterface.SetVisibility` rétegnevei (Unity WebInterface.md,
 * "SetVisibility / GetVisibility"), és ahogy az eszköztár „Rétegek" menüje mutatja őket.
 *
 * Minden alkatrész pontosan egy rétegen van, és csak a saját rétege dönti el, látszik-e (a front lap
 * nélkül is ott marad a fogantyú). A menü sorai a Unity csoportjai: a „Frontok" a front lapok a
 * fogantyúkkal és pántokkal együtt.
 *
 * A lap mindig a TELJES állapotot küldi, nem csak a változást - `[{"All": true}, {<rejtett>: false}...]` -:
 * az editor módú transport metódusnév szerint összevonja a sorban álló hívásokat (az utolsó nyer), így két
 * gyors részleges kérésből az első elveszne. A teljes állapot idempotens, az utolsó mindig a helyes.
 */

/** Rétegnév → látszik-e. */
export type VisibilityMap = Record<string, boolean>;

/** A Unity rétegei, ahogy a `Visibility` view change felsorolja őket. */
export const LAYERS = [
    'FrontBoards',
    'Handles', 'Hinges', 'JointFittings', 'WallHangers', 'DrawerHardware', 'OtherHardware', 'Legs',
    'Ovens', 'Hobs', 'Sinks', 'Taps', 'Extractors', 'Microwaves', 'Dishwashers', 'Fridges', 'WashingMachines',
    'Dryers', 'OtherAppliances',
    'WorktopBoards', 'Backs', 'ToeKicks', 'CarcassBoards', 'Shelves', 'DrawerBoxes',
    'Walls', 'Floor', 'Ceiling',
] as const;

/** A Unity csoportjai közül azok, amelyeket a menü használ (a Unity `GetVisibility` `groups`-a szerint). */
const HARDWARE = ['Handles', 'Hinges', 'JointFittings', 'WallHangers', 'DrawerHardware', 'OtherHardware', 'Legs'];
const WORKTOP_APPLIANCES = ['Hobs', 'Sinks', 'Taps'];
const APPLIANCES = ['Ovens', ...WORKTOP_APPLIANCES, 'Extractors', 'Microwaves', 'Dishwashers', 'Fridges',
    'WashingMachines', 'Dryers', 'OtherAppliances'];

/** Egy sor a menüben: a Unity egy csoportja vagy rétege, és a rétegek, amelyeket takar. */
export interface VisibilityRow {
    id: string;
    label: string;
    layers: readonly string[];
}

export const VISIBILITY_ROWS: VisibilityRow[] = [
    { id: 'Fronts',     label: 'Frontok',        layers: ['FrontBoards', 'Handles', 'Hinges'] },
    { id: 'Hardware',   label: 'Vasalatok',      layers: HARDWARE },
    { id: 'Appliances', label: 'Gépek',          layers: APPLIANCES },
    { id: 'Worktop',    label: 'Munkalap',       layers: ['WorktopBoards', ...WORKTOP_APPLIANCES] },
    { id: 'Backs',      label: 'Hátfalak',       layers: ['Backs'] },
    { id: 'Plinth',     label: 'Lábazat',        layers: ['ToeKicks', 'Legs'] },
    { id: 'Carcass',    label: 'Korpusz',        layers: ['CarcassBoards', 'JointFittings', 'WallHangers'] },
    { id: 'Interior',   label: 'Polcok, fiókok', layers: ['Shelves', 'DrawerBoxes', 'DrawerHardware'] },
    { id: 'Room',       label: 'Szoba',          layers: ['Walls', 'Floor', 'Ceiling'] },
];

/** Minden réteg látszik — a Unity kezdőállapota. */
export const ALL_VISIBLE: VisibilityMap = Object.fromEntries(LAYERS.map((layer) => [layer, true]));

/** Egy sor rétegei mind látszanak, egyik sem, vagy csak egy részük (pl. a vasalatok, ha a frontok ki vannak kapcsolva). */
export type RowState = 'shown' | 'hidden' | 'partial';

export const rowState = (row: VisibilityRow, map: VisibilityMap): RowState => {
    const shown = row.layers.filter((layer) => map[layer] !== false).length;
    return shown === row.layers.length ? 'shown' : shown === 0 ? 'hidden' : 'partial';
};

export const FRONTS_ROW: VisibilityRow = VISIBILITY_ROWS.find((row) => row.id === 'Fronts')!;

export const anyHidden = (map: VisibilityMap): boolean => LAYERS.some((layer) => map[layer] === false);

/** A térkép egy sor rétegeivel átállítva. */
export const withRow = (map: VisibilityMap, row: VisibilityRow, shown: boolean): VisibilityMap => ({
    ...map,
    ...Object.fromEntries(row.layers.map((layer) => [layer, shown])),
});

/** A Unity `Visibility` view change értéke (JSON térkép) → VisibilityMap; hibás értékre null. */
export const parseVisibility = (value: string): VisibilityMap | null => {
    try {
        const parsed = JSON.parse(value);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
        return { ...ALL_VISIBLE, ...parsed };
    } catch {
        return null;
    }
};

/** A `SetVisibility` argumentuma: minden látszik, aztán a rejtett rétegek egyenként. */
export const visibilityRequest = (map: VisibilityMap): string =>
    JSON.stringify([{ All: true }, ...LAYERS.filter((layer) => map[layer] === false).map((layer) => ({ [layer]: false }))]);
